import { Alert } from "@mui/material";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useTimestampFormatActions } from "@/shared/components/RobotoMarkdown/timeFormat";
import {
  AgentThreadRole,
  AgentThreadStatus,
  isAgentThreadTextContent,
  type ClientViewingContext,
} from "@/shared/domain/agentThread/AgentThreadRecord";
import {
  ApprovalMode,
  effectiveApprovalMode,
} from "@/shared/domain/agentThread/clientTools/approvalModes";
import { useActiveAgentThread } from "@/shared/domain/agentThread/hooks/useActiveAgentThread";
import { useCancelGeneration } from "@/shared/domain/agentThread/hooks/useCancelGeneration";
import { useClientToolExecution } from "@/shared/domain/agentThread/hooks/useClientToolExecution";
import { useReleasedClientTools } from "@/shared/domain/agentThread/hooks/useReleasedClientTools";
import { useRetryFailedTurn } from "@/shared/domain/agentThread/hooks/useRetryFailedTurn";
import { useSavedToolPermissions } from "@/shared/domain/agentThread/hooks/useSavedToolPermissions";
import { useSendMessage } from "@/shared/domain/agentThread/hooks/useSendMessage";
import { useServerViewWrites } from "@/shared/domain/agentThread/hooks/useServerViewWrites";
import { useStartAgentThread } from "@/shared/domain/agentThread/hooks/useStartAgentThread";
import {
  subscribePendingKickoff,
  type PendingKickoff,
} from "@/shared/domain/agentThread/pendingKickoff";
import {
  RobotoConflictException,
  RobotoInvalidRequestException,
} from "@/shared/http";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import styles from "./AgentThread.module.css";
import { AgentThreadByIdView, type RetryRefusal } from "./AgentThreadByIdView";
import { AgentThreadInputBox } from "./AgentThreadInputBox";
import { useAIAgentThread } from "./AIAgentThreadContext";
import { ClientToolSettingsModal } from "./ClientToolSettingsModal";
import { NewAgentThreadView } from "./NewAgentThreadView";
import { withDisplayAnchor } from "./timeFormat/withDisplayAnchor";

interface AgentThreadProps {
  /**
   * Watch the thread but execute none of its client tools.
   *
   * Set by the thread detail page once a navigation has handed the thread to
   * the chat sidebar. Both copies are mounted for that moment and both see the
   * same pending batch; suspending the departing one gives the batch to the
   * sidebar, which survives the navigation, instead of to whichever executor
   * claims the tool_use ids first.
   */
  clientToolsSuspended?: boolean;
}

/**
 * AgentThread component orchestrates all data fetching and mutations.
 * It conditionally renders NewAgentThreadView vs AgentThreadByIdView based on
 * whether threadId exists. AgentThreadInputBox is rendered as a pure form
 * component with callbacks.
 */
export const AgentThread: React.FC<AgentThreadProps> = ({
  clientToolsSuspended = false,
}) => {
  const orgId = useCurrentOrgId();
  const {
    currentThreadId,
    navigateToThread,
    getContextSnapshot,
    modelProfile,
    autoEnabled,
    handleToggleAuto,
  } = useAIAgentThread();

  // Data fetching
  const agentThreadQuery = useActiveAgentThread(currentThreadId, {
    orgId,
    loadMessages: true,
  });
  const agentThread = agentThreadQuery.data;

  // Saved tool permissions, read here and changed in the settings modal.
  const { permissions: savedToolPermissions } = useSavedToolPermissions();

  // Client-side tool execution with approval gating
  const { pendingApproval, handleApprove, handleDecline } =
    useClientToolExecution(
      agentThread,
      orgId,
      autoEnabled,
      savedToolPermissions,
      { suspended: clientToolsSuspended },
    );

  // View writes run on the server, so the page learns of them from the thread.
  useServerViewWrites(agentThread, orgId);

  // Whether to offer "turn on Auto" alongside the pending approval. Offered
  // only for a tool that follows the setting: a tool pinned to
  // REQUIRE_APPROVAL prompts either way, as does every tool while the saved
  // permissions are not loaded, so turning on Auto would not stop the prompt.
  // The card gets `handleToggleAuto` because it owns the sessionStorage
  // write; the `autoEnabled` guard means the toggle can only turn Auto on.
  const canSkipNextTimeWithAuto =
    !autoEnabled &&
    pendingApproval !== null &&
    effectiveApprovalMode(pendingApproval.tool, savedToolPermissions) ===
      ApprovalMode.FOLLOW_AUTO;

  // Settings modal state (opened from confirmation modal gear icon)
  const [settingsModalOpen, setSettingsModalOpen] = React.useState(false);

  // Mutations
  const { specs: clientTools } = useReleasedClientTools();
  const startAgentThreadMutation = useStartAgentThread();
  const sendMessageMutation = useSendMessage();
  const cancelGenerationMutation = useCancelGeneration();
  const retryFailedTurnMutation = useRetryFailedTurn();
  const [retryRefusal, setRetryRefusal] = React.useState<RetryRefusal>();

  // Cancellation pending state - cleared when backend confirms cancellation
  const [isCancelling, setIsCancelling] = React.useState(false);
  const isSubmittingRef = React.useRef(false);

  // Clear cancellation state when thread status changes to USER_TURN
  React.useEffect(() => {
    if (agentThread?.status === AgentThreadStatus.USER_TURN) {
      setIsCancelling(false);
    }
  }, [agentThread?.status]);

  // Derived state: the thread is "loading" when it exists and isn't the user's
  // turn, OR when the thread query is in its initial loading state (bridges the
  // gap between startAgentThread completing and the query resolving).
  const loading =
    agentThreadQuery.isLoading ||
    (agentThreadQuery.data !== undefined &&
      agentThreadQuery.data?.status !== AgentThreadStatus.USER_TURN);

  // The reader's t=0 and the name they see for it ride along with every send,
  // so a relative time they type resolves against the same origin their screen
  // is using and the agent can name it back to them. Read through the actions
  // context, and only at send time: a thread following the visualizer moves its
  // t=0 as the workspace loads other files, and subscribing to the format
  // itself would re-render the whole transcript — re-parsing every message's
  // markdown — each time the reader changed how timestamps look.
  const timestampFormatActions = useTimestampFormatActions();

  // Mutation handlers
  const handleStartAgentThread = (
    message: string,
    chosenModelProfile: string | undefined,
    requestDetails?: string,
  ) => {
    // Use mutate + onSuccess instead of mutateAsync so React batches
    // the isPending→false and navigateToThread address updates in the
    // same render, preventing a flash of the send button.
    return new Promise<void>((resolve, reject) => {
      startAgentThreadMutation.mutate(
        {
          message,
          orgId,
          clientContext: withRequestDetails(
            withDisplayAnchor(
              getContextSnapshot(),
              timestampFormatActions?.getDisplayAnchor(),
            ),
            requestDetails,
          ),
          modelProfile: chosenModelProfile,
          clientTools,
        },
        {
          onSuccess: (agentThreadRecord) => {
            navigateToThread(agentThreadRecord.thread_id);
            resolve();
          },
          onError: (error) => reject(error),
        },
      );
    });
  };

  // A button outside the sidebar (such as "Build with chat" on the agents
  // list) stashes a first message with `stashPendingKickoff`. Each kickoff
  // starts a new thread with that message, and with the kickoff's request
  // details in the viewing context, whatever thread was open before. The
  // person asked to start over with this request, so the new thread also
  // ignores any model they picked for the thread it replaces and starts on the
  // org default.
  //
  // `subscribePendingKickoff` delivers both a kickoff stashed before mount and
  // any stashed while mounted. The sidebar subscribes once per mount, and the
  // handler calls `runKickoffRef.current`, which is reassigned after every
  // render, so each kickoff runs with the latest `orgId` and
  // `handleStartAgentThread`.
  const runKickoffRef = React.useRef<(kickoff: PendingKickoff) => void>(
    () => {},
  );
  React.useEffect(() => {
    runKickoffRef.current = (kickoff: PendingKickoff) => {
      navigateToThread(undefined);
      void handleStartAgentThread(
        kickoff.message,
        undefined,
        kickoff.requestDetails,
      ).catch((error) => {
        // The start mutation's error alert shows the person the failure; this
        // reports it to error monitoring instead of leaving an unhandled
        // rejection.
        ErrorMonitoringService.captureError(error);
      });
    };
  });

  React.useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;

    // Subscribe one microtask after mount, because subscribing can start a
    // thread at once from a stashed kickoff. In development, StrictMode runs
    // every effect's setup, cleanup and setup again synchronously at mount.
    // That cleanup unsubscribes `useStartAgentThread`'s observer, which
    // detaches it from a thread start already in flight, so the start would
    // never report success or failure and the composer would stay disabled.
    // By the microtask both setups have run, and the first setup's `active` is
    // false, so only the second subscribes.
    queueMicrotask(() => {
      if (!active) {
        return;
      }
      unsubscribe = subscribePendingKickoff((kickoff) => {
        runKickoffRef.current(kickoff);
      });
    });

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  const handleSendMessage = async (message: string) => {
    if (!currentThreadId) {
      return;
    }

    await sendMessageMutation.mutateAsync({
      threadId: currentThreadId,
      message: message,
      orgId,
      clientContext: withDisplayAnchor(
        getContextSnapshot(),
        timestampFormatActions?.getDisplayAnchor(),
      ),
      clientTools,
    });
  };

  // Callbacks for AgentThreadInput
  const handleSubmit = async (message: string): Promise<void> => {
    const shouldSendMessage =
      currentThreadId !== undefined && !sendMessageMutation.isPending;
    const shouldStartAgentThread =
      currentThreadId === undefined &&
      message.trim() !== "" &&
      !startAgentThreadMutation.isPending;

    if (
      isSubmittingRef.current ||
      (!shouldSendMessage && !shouldStartAgentThread)
    ) {
      return;
    }

    isSubmittingRef.current = true;
    // A new message moves the conversation past the failure notice, so why a
    // retry from it did not run no longer matters.
    setRetryRefusal(undefined);

    try {
      if (shouldSendMessage) {
        await handleSendMessage(message);
      } else {
        await handleStartAgentThread(message, modelProfile);
      }
    } catch (err) {
      ErrorMonitoringService.captureError(err);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleExampleClick = async (question: string): Promise<void> => {
    if (startAgentThreadMutation.isPending) {
      return;
    }
    try {
      await handleStartAgentThread(question, modelProfile);
    } catch (err) {
      ErrorMonitoringService.captureError(err);
    }
  };

  const handleCancel = async () => {
    // Reset mutations to cancel any pending requests
    startAgentThreadMutation.reset();
    sendMessageMutation.reset();

    // If we have an active thread that's generating, request cancellation from the backend
    if (
      currentThreadId &&
      agentThread?.status !== AgentThreadStatus.USER_TURN
    ) {
      setIsCancelling(true);
      try {
        await cancelGenerationMutation.mutateAsync({
          threadId: currentThreadId,
        });
      } catch (err) {
        setIsCancelling(false);
        ErrorMonitoringService.captureError(err);
      }
    }
  };

  // Two things fail in a thread, and Retry means something different for
  // each: a message the server never received is sent again, and a turn that
  // failed on the server is run again there.
  const handleRetry = React.useCallback(
    (messageSequenceNum: number) => {
      if (!agentThread) {
        return;
      }

      const message = agentThread.messages[messageSequenceNum];
      if (message?.role === AgentThreadRole.USER) {
        const textContent = message.content.find(isAgentThreadTextContent);
        if (textContent === undefined) {
          return;
        }
        sendMessageMutation.reset();
        void sendMessageMutation.retry(agentThread.thread_id, textContent.text);
        return;
      }

      setRetryRefusal(undefined);
      retryFailedTurnMutation
        .mutateAsync({ threadId: agentThread.thread_id, orgId })
        .catch((err: unknown) => {
          setRetryRefusal({
            threadId: agentThread.thread_id,
            messageSequenceNum,
            explanation: explainRetryRefusal(err),
          });
          ErrorMonitoringService.captureError(err);
        });
    },
    [agentThread, orgId, retryFailedTurnMutation, sendMessageMutation],
  );

  const sending =
    startAgentThreadMutation.isPending || sendMessageMutation.isPending;
  const isGenerating = loading;

  return (
    <>
      <div className={styles.content}>
        {/* Error Alert */}
        {startAgentThreadMutation.isError ? (
          <Alert
            severity="error"
            onClose={() => {
              startAgentThreadMutation.reset();
            }}
            className={styles.errorAlert}
          >
            {startAgentThreadMutation.error?.message || "An error occurred"}
          </Alert>
        ) : null}
        {/* GOALS_FAILED banner — surfaces when the agent runner exhausts its
            corrective-reprompt budget without satisfying every declared goal. */}
        {agentThread?.status === AgentThreadStatus.GOALS_FAILED ? (
          <Alert severity="error" className={styles.errorAlert}>
            The agent could not achieve all declared goals for this turn.
            Inspect the transcript to see which goals failed.
          </Alert>
        ) : null}
        {/* Conditional Views */}
        <div className={styles.agentThreadView}>
          {agentThread ? (
            // Keyed by thread because this view holds state that means
            // something only for the conversation it was measured against:
            // scroll position, the first-run auto-scroll latch, an open jump
            // target. The provider above deliberately does not remount on a
            // thread change — that would tear down the client-tool executor
            // mid-batch — so the reset belongs here, below it.
            <AgentThreadByIdView
              key={agentThread.thread_id}
              agentThread={agentThread}
              isCancelling={isCancelling}
              onRetry={handleRetry}
              retryRefusal={retryRefusal}
              pendingApproval={pendingApproval}
              onApprove={() => void handleApprove()}
              onDecline={handleDecline}
              onEnableAutoMode={
                canSkipNextTimeWithAuto ? handleToggleAuto : undefined
              }
              savedPermissionsUnavailable={
                savedToolPermissions.status === "unavailable"
              }
            />
          ) : (
            <NewAgentThreadView onExampleClick={handleExampleClick} />
          )}
        </div>
      </div>

      <div className={styles.inputContainer}>
        <AgentThreadInputBox
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={sending || isGenerating}
          isCancelling={isCancelling}
          agentThreadModelProfile={agentThread?.model_profile}
          onAgentThreadCreatedFromSkill={(sessionId) =>
            navigateToThread(sessionId)
          }
        />
      </div>

      {/* Settings modal (can be opened from approval card or AutoToggle gear) */}
      <ClientToolSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
      />
    </>
  );
};

/**
 * The viewing context with a kickoff's request details added as
 * `misc_context.request_details` (see `PendingKickoff`), or unchanged when
 * there are none.
 */
function withRequestDetails(
  context: ClientViewingContext,
  requestDetails: string | undefined,
): ClientViewingContext {
  if (requestDetails === undefined) {
    return context;
  }
  return {
    ...context,
    misc_context: {
      ...context.misc_context,
      request_details: requestDetails,
    },
  };
}

/** What the person is told when retrying a failed turn throws `error`. */
function explainRetryRefusal(error: unknown): string {
  if (error instanceof RobotoConflictException) {
    return "This reply was already retried. The new reply shows up below as it comes in.";
  }
  if (error instanceof RobotoInvalidRequestException) {
    return "This reply can't be retried because the conversation continued after it. Send a new message instead.";
  }
  return "Retry didn't go through. Try again.";
}
